import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, Square } from "lucide-react";
import { PageHeader, Loader, StatCard } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { fmtPKR, fmtDate } from "@/lib/format";
import { fmtPKT } from "@/components/aviator/verify";

const FIELDS = [
  ["min_bet", "Minimum participation (Rs)", "number"], ["max_bet", "Maximum participation (Rs)", "number"],
  ["countdown_seconds", "Countdown (seconds)", "number"], ["round_duration_seconds", "Max flight duration (seconds)", "number"],
  ["post_round_seconds", "Result display (seconds)", "number"], ["growth_rate", "Multiplier growth rate (per second)", "number"],
  ["max_multiplier", "Maximum multiplier (x)", "number"], ["start_date", "Start date", "date"],
  ["end_date", "End date", "date"], ["open_time", "Daily open (PKT)", "time"], ["close_time", "Daily close (PKT)", "time"],
];

const ConfigForm = ({ cfg, onSaved }) => {
  const [f, setF] = useState(cfg);
  const save = async () => {
    const body = { enabled: f.enabled };
    FIELDS.forEach(([k, , type]) => { body[k] = type === "number" ? Number(f[k]) : (f[k] || ""); });
    try { await api.put("/admin/aviator/config", body); toast.success("Aviator settings saved"); onSaved(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="ec-card p-6" data-testid="aviator-config-form">
      <div className="flex items-center justify-between mb-5">
        <Label>Game Enabled</Label>
        <Switch checked={!!f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="aviator-enabled-switch" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, type]) => (
          <div key={k}><Label className="text-xs">{label}</Label>
            <Input type={type} step="any" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`aviator-cfg-${k}`} /></div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-3">Open = Close means open all day. Changes apply to the next round; a running round is never altered.</p>
      <Button onClick={save} className="mt-4 w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold" data-testid="aviator-save-config">Save Settings</Button>
    </div>
  );
};

const SessionControls = ({ data, reload }) => {
  const [until, setUntil] = useState("");
  const [note, setNote] = useState("");
  const call = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const s = data.config.session_status;
  return (
    <div className="ec-card p-6 space-y-5" data-testid="aviator-session-controls">
      <div>
        <p className="text-xs text-gray-500 mb-2">Session: <span className="text-white font-semibold uppercase" data-testid="aviator-session-status">{s}</span>
          {" · "}{data.schedule.open ? <span className="text-emerald-400">Open</span> : <span className="text-red-400">Closed ({data.schedule.closed_reason})</span>}</p>
        <div className="grid grid-cols-3 gap-2">
          <Button onClick={() => call(() => api.post("/admin/aviator/session/start"), "Session started")} disabled={s === "running"} className="bg-emerald-600 hover:bg-emerald-500" data-testid="aviator-start-btn"><Play className="w-4 h-4 me-1" />Start</Button>
          <Button onClick={() => call(() => api.post("/admin/aviator/session/pause"), "Session paused")} disabled={s === "paused"} className="bg-yellow-600 hover:bg-yellow-500" data-testid="aviator-pause-btn"><Pause className="w-4 h-4 me-1" />Pause</Button>
          <Button onClick={() => call(() => api.post("/admin/aviator/session/stop"), "Session stopped")} disabled={s === "stopped"} className="bg-red-600 hover:bg-red-500" data-testid="aviator-stop-btn"><Square className="w-4 h-4 me-1" />Stop</Button>
        </div>
      </div>
      <div>
        <Label className="text-xs">Postpone until (PKT)</Label>
        <Input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1" data-testid="aviator-postpone-until" />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Message for members (optional)" className="mt-2" data-testid="aviator-postpone-note-input" />
        <div className="flex gap-2 mt-2">
          <Button onClick={() => until ? call(() => api.put("/admin/aviator/postpone", { until, note }), "Session postponed") : toast.error("Pick a date and time")} className="flex-1 bg-orange-600 hover:bg-orange-500" data-testid="aviator-postpone-btn">Postpone</Button>
          <Button variant="outline" onClick={() => call(() => api.put("/admin/aviator/postpone", { until: null }), "Postponement cleared")} className="border-white/10 bg-white/5" data-testid="aviator-clear-postpone-btn">Clear</Button>
        </div>
        {data.config.postponed_until && <p className="text-amber-400 text-xs mt-2" data-testid="aviator-admin-postponed">Postponed until {fmtPKT(data.config.postponed_until)}</p>}
      </div>
    </div>
  );
};

const List = ({ title, rows, render, empty, testid }) => (
  <div className="ec-card p-6" data-testid={testid}>
    <h3 className="font-head text-lg font-semibold text-white mb-4">{title}</h3>
    {rows.length === 0 ? <p className="text-gray-500 text-sm py-6 text-center">{empty}</p> :
      <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">{rows.map(render)}</div>}
  </div>
);

export default function AdminAviator() {
  const [data, setData] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [bets, setBets] = useState([]);
  const load = () => {
    api.get("/admin/aviator").then(({ data }) => setData(data)).catch(() => {});
    api.get("/admin/aviator/rounds").then(({ data }) => setRounds(data)).catch(() => {});
    api.get("/admin/aviator/bets").then(({ data }) => setBets(data)).catch(() => {});
  };
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, []);
  if (!data) return <Loader />;
  const rnd = data.round;
  return (
    <div>
      <PageHeader title="Aviator Game Management" subtitle="Control sessions, schedule, limits and review every round." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="aviator-stat-round" label="Current Round" value={rnd ? `#${rnd.round_no} · ${rnd.status}` : "None"} />
        <StatCard testid="aviator-stat-participants" label="Active Participants" value={data.participants.length} />
        <StatCard testid="aviator-stat-completed" label="Completed Rounds" value={data.completed_rounds} />
        <StatCard testid="aviator-stat-net" label="Entries / Payouts" value={`${fmtPKR(data.total_amount)} / ${fmtPKR(data.total_payout)}`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <ConfigForm key={JSON.stringify(data.config)} cfg={data.config} onSaved={load} />
        <div className="space-y-6">
          <SessionControls data={data} reload={load} />
          <List title="Current Round Participants" rows={data.participants} empty="No participants in this round." testid="aviator-participants"
            render={(b) => <div key={b.id} className="flex justify-between py-2 text-sm"><span className="text-white">{b.user_name}</span><span className="text-gray-400">{fmtPKR(b.amount)} · {b.status}</span></div>} />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <List title="Round History" rows={rounds} empty="No rounds yet." testid="aviator-admin-rounds"
          render={(r) => (
            <div key={r.id} className="py-2 text-sm">
              <div className="flex justify-between"><span className="text-white">#{r.round_no} · {r.status === "cancelled" ? "Cancelled" : `${r.crash_point.toFixed(2)}x`}</span><span className="text-gray-400">{r.players} players · {fmtPKR(r.total_amount)} in / {fmtPKR(r.total_payout)} out</span></div>
              <p className="text-[10px] text-gray-600 truncate keep-ltr">Seed {r.server_seed} · {fmtDate(r.completed_at)}</p>
            </div>)} />
        <List title="All Entries" rows={bets} empty="No entries yet." testid="aviator-admin-bets"
          render={(b) => (
            <div key={b.id} className="flex justify-between py-2 text-sm">
              <span className="text-white">#{b.round_no} · {b.user_name}</span>
              <span className="text-gray-400">{fmtPKR(b.amount)} · {b.status}{b.status === "cashed_out" ? ` @ ${b.cashout_multiplier.toFixed(2)}x` : ""}</span>
            </div>)} />
      </div>
    </div>
  );
}
